import React, { useEffect, useMemo, useRef, useState } from 'react';
import Styled from './styled';
import {
    isAllowedUrl,
    useTipTapViewer
} from '../../utils/TipTap';
import { EditorContent } from '@tiptap/react';
import { ReceiptIcon, ProductCard } from '../index';
import { MessageParticipantRole } from '@amzn/ring-cs-chatbot-service-model';
import { MessageProvider } from '../../utils/MessageContext';
import { useSessionContext } from '../../utils/SessionContext';

const MAX_KEY_DELAY_MS = 200; // Maximum delay between characters
const MIN_KEY_DELAY_MS = 50; // Minimum delay between characters
const STREAMING_CONTENT_GAP_MS = 1500; // Gap between streaming content

class StreamManager {
    private content: string;
    private displayedIndexFloat: number;
    private onUpdate: (displayedContent: string) => void;
    private intervalId?: ReturnType<typeof setInterval>;

    constructor (initialContent: string, onUpdate: (displayedContent: string) => void) {
        this.content = initialContent;
        this.displayedIndexFloat = this.content.length;
        this.onUpdate = onUpdate;
        this.intervalId = undefined;
    }

    public updateContent (content: string) {
        this.content = content;
        if (this.displayedIndexFloat >= this.content.length) {
            this.displayedIndexFloat = this.content.length;
            this.updateDisplayedContent();
            return;
        } else {
            this.updateDisplayedContent();
        }

        // stop any existing interval
        clearInterval(this.intervalId);
        this.intervalId = undefined;

        const remainingKeys = Math.max(0, this.content.length - this.displayedIndexFloat);
        const intervalMs = Math.max(MIN_KEY_DELAY_MS, Math.min(MAX_KEY_DELAY_MS, STREAMING_CONTENT_GAP_MS / remainingKeys));
        const keyDelta = Math.max(1, remainingKeys / (STREAMING_CONTENT_GAP_MS / intervalMs));

        this.intervalId = setInterval(() => {
            if (this.displayedIndexFloat < this.content.length) {
                this.displayedIndexFloat = Math.min(this.content.length, this.displayedIndexFloat + keyDelta);
                this.updateDisplayedContent();
            } else {
                // stop when done
                clearInterval(this.intervalId);
                this.intervalId = undefined;
            }
        }, intervalMs);
    }

    public destroy () {
        clearInterval(this.intervalId);
        this.intervalId = undefined;
    }

    private updateDisplayedContent () {
        const displayedContent = this.content.substring(0, Math.round(this.displayedIndexFloat));
        this.onUpdate(displayedContent);
    }
}

const TipTapContent: React.FC<{
    message: string;
    textOnly: boolean;
    ariaLabel: string;
    selectedOption?: string;
    sendMessage: (text: string) => Promise<void>;
    isActionable?: boolean;
}> = ({
    message,
    textOnly,
    ariaLabel,
    selectedOption,
    sendMessage,
    isActionable,
}) => {
    const viewer = useTipTapViewer({
        message,
        textOnly,
        ariaLabel,
    });

    return (
        <MessageProvider
            selectedOption={selectedOption}
            sendMessage={sendMessage}
            isActionable={isActionable}
        >
            <EditorContent editor={viewer} />
        </MessageProvider>
    );
};

export type BubbleProps = {
    /** Accessibility support (alt text/aria-label, defaults to 'chat message') */
    ariaLabel?: string;
    /** The status of the badge, defaults to 'bot'. */
    badge?: MessageParticipantRole;
    /** Optional text for the badge. */
    badgeText?: string;
    /** Determines chat bubble alignment - left (false) | right (true) */
    customer?: boolean;
    /** Optional, isActionable boolean which we pass to TipTapViewer */
    isActionable?: boolean;
    /** Optional content to display as final message content. */
    content?: string;
    /** Optional VQA events to display below the main content */
    events?: Array<{
        eventId: string;
    }>;
    /** Optional title for events section (defaults to 'Recent Events') */
    eventsTitle?: string;
    /** Optional product cards to display below the main content */
    products?: Array<{
        desc: string;
        imageUrl: string;
        markdownPrice?: string;
        price: string;
        slug: string;
        title: string;
    }>;
    /** Optional title for products section (defaults to 'Recommended Products') */
    productsTitle?: string;
    /** When true, displays message content with character-by-character streaming animation. */
    reasoning?: boolean;
    /** Optional receipt status: 'DELIVERED', 'READ', or undefined */
    receipt?: 'DELIVERED' | 'READ';
    /** Optional sources (title and url) to display below the main content */
    sources?: Array<{ title: string; url: string }>;
    /** Optional title for sources section (defaults to 'Helpful Links') */
    sourceTitle?: string;
    /** Optional, selectedOptions which we pass to TipTapViewer */
    selectedOption?: string;
    /** Send Chat Message via Viewer Components (ex: Select) */
    sendMessage: (text: string) => Promise<void>;
    /** If true, render as plain text without TipTap processing */
    textOnly?: boolean;
};

/**
 * Chat Bubble component, orientation of the bubble is based on customer prop.
 * Badge is optional and will appear at the top of the chat bubble
 *
 * @param {BubbleProps} props - The properties to configure the Bubble component.
 * @returns {JSX.Element} The rendered Bubble component.
 */
export const Bubble: React.FC<BubbleProps> = ({
    ariaLabel = 'chat message',
    badge,
    badgeText,
    customer = false,
    content,
    events,
    eventsTitle = 'Recent Events',
    isActionable = true,
    products,
    productsTitle = 'Recommended Products',
    reasoning = false,
    receipt,
    sources,
    sourceTitle = 'Helpful Links',
    selectedOption,
    sendMessage,
    textOnly = false,
}) => {
    const hasContent = !!content && !!content.trim();
    const isStreaming = reasoning && hasContent;

    // Only render if there's actual content (non-empty strings)
    if (!hasContent) return null;

    const { sessionId } = useSessionContext();
    const [streamedContent, setStreamedContent] = useState(content);
    const streamManagerRef = useRef<StreamManager | null>(null);

    // Create StreamManager once on mount
    useEffect(() => {
        streamManagerRef.current = new StreamManager(content || '', setStreamedContent);
        return () => {
            streamManagerRef.current!.destroy();
        }
    }, []);

    // Create once, destroy on unmount
    useEffect(() => {
        if (streamManagerRef.current) {
            streamManagerRef.current.updateContent(content || '');
        }
    }, [content]);

    // Filter sources to only include allowed urls
    const allowedSources = useMemo(() => {
        return sources?.filter((source) => isAllowedUrl(source.url)) || [];
    }, [sources]);

    // Filter events to only include allowed urls
    const allowedEvents = useMemo(() => {
        return events?.filter((event) => event.eventId) || [];
    }, [events]);

    return (
        <Styled.Wrapper customer={customer}>
            <Styled.Bubble customer={customer}>
                {badge && <Styled.Badge>{badgeText}</Styled.Badge>}
                <Styled.Content>
                    {textOnly ?
                        // Plain text rendering
                        <div
                            aria-label={ariaLabel}
                            role='textbox'
                            aria-readonly='true'
                        >
                            {isStreaming ? streamedContent : content}
                        </div> :
                        // Rich content rendering usingTipTap with MessageProvider
                        <TipTapContent
                            message={content}
                            textOnly={customer}
                            ariaLabel={ariaLabel}
                            selectedOption={selectedOption}
                            sendMessage={sendMessage}
                            isActionable={isActionable}
                        />
                    }
                </Styled.Content>
                {allowedSources.length > 0 &&
                    <>
                        <Styled.Divider />
                        <Styled.SourceTitle>{sourceTitle}:</Styled.SourceTitle>
                        <Styled.Sources>
                            <ul>
                                {allowedSources.map((source, index) => (
                                    <li key={index}>
                                        <a href={source.url} target='_blank' rel='noopener noreferrer'>
                                            {source.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </Styled.Sources>
                    </>
                }
                {/* TODO: This is just a placeholder for the events section. Needs to be updated once we can play around with actual events */}
                {allowedEvents.length > 0 && (
                    <>
                        <Styled.Divider />
                        <Styled.SourceTitle>{eventsTitle}:</Styled.SourceTitle>
                        <Styled.Events>
                            {allowedEvents.map((event) => (
                                <Styled.EventCard key={event.eventId}>
                                    <Styled.EventDetails>
                                        {event.eventId}
                                    </Styled.EventDetails>
                                </Styled.EventCard>
                            ))}
                        </Styled.Events>
                    </>
                )}
                {products && products.length > 0 && (
                    <>
                        <Styled.Divider />
                        <Styled.SourceTitle>{productsTitle}:</Styled.SourceTitle>
                        <div>
                            {products.map((product, index) => (
                                <ProductCard
                                    key={index}
                                    title={product.title}
                                    subtext={product.desc}
                                    price={product.price}
                                    markdownPrice={product.markdownPrice}
                                    imageUrl={product.imageUrl}
                                    onClick={() => {
                                        if (product.title) {
                                            window.open(`https://ring.com/products/${product.slug}?chatBotSessionId=${sessionId}`, '_blank');
                                        }
                                    }}
                                    disabled={!product.title}
                                />
                            ))}
                        </div>
                    </>
                )}
                {receipt &&
                    <Styled.ReceiptIndicator receipt={receipt}>
                        <ReceiptIcon />
                    </Styled.ReceiptIndicator>
                }
            </Styled.Bubble>
        </Styled.Wrapper>
    );
};
